import React from 'react';

// ─── Shared display primitives for all lens result panels ─────────────────────

// Simple horizontal bar — value as % of max
export function DistributionBar({ label, count, max, color = '#1d4ed8' }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 3 }}>
        <span style={{ color: '#374151', fontWeight: 500 }}>{label}</span>
        <span style={{ color: '#6b7280', fontWeight: 600 }}>{count}</span>
      </div>
      <div style={{ background: '#f3f4f6', borderRadius: 4, height: 10, overflow: 'hidden' }}>
        <div
          style={{
            width: `${pct}%`, height: '100%',
            background: color, borderRadius: 4,
            transition: 'width .4s ease',
          }}
        />
      </div>
    </div>
  );
}

// Ranked list with optional colour indicator
export function RankedList({ items, labelKey = '_id', countKey = 'count', colorFn, emptyMsg = 'No data.' }) {
  if (!items?.length) return <p style={{ color: '#6b7280', fontSize: 13 }}>{emptyMsg}</p>;
  const max = Math.max(...items.map((i) => i[countKey]));
  return (
    <div>
      {items.map((item, idx) => (
        <DistributionBar
          key={idx}
          label={item[labelKey] || '(unknown)'}
          count={item[countKey]}
          max={max}
          color={colorFn ? colorFn(item) : '#1d4ed8'}
        />
      ))}
    </div>
  );
}

// Small stat card
export function StatCard({ value, label, sub, color = '#1d4ed8' }) {
  return (
    <div
      style={{
        background: '#fff', border: '1px solid #e5e7eb',
        borderRadius: 10, padding: '16px 20px',
        borderTop: `4px solid ${color}`,
        minWidth: 120,
      }}
    >
      <div style={{ fontSize: 28, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// Section heading inside a lens panel
export function LensSection({ title, children, style = {} }) {
  return (
    <div style={{ marginBottom: 28, ...style }}>
      <h3
        style={{
          fontSize: 15, fontWeight: 700, color: '#1f2937',
          borderBottom: '2px solid #e5e7eb', paddingBottom: 8, marginBottom: 14,
        }}
      >
        {title}
      </h3>
      {children}
    </div>
  );
}

// Compact record row used in "recent" lists
export function RecordRow({ icon, primary, secondary, meta, badge }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 12,
        padding: '10px 14px', borderBottom: '1px solid #f3f4f6',
      }}
    >
      <span style={{ fontSize: 22, flexShrink: 0 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>{primary}</div>
        {secondary && (
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 1 }}>{secondary}</div>
        )}
        {meta && (
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{meta}</div>
        )}
      </div>
      {badge && <div style={{ flexShrink: 0 }}>{badge}</div>}
    </div>
  );
}

// Month-of-year calendar heatmap (12 cells, Jan–Dec)
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export function MonthHeatmap({ byMonth, color = '#16a34a' }) {
  // byMonth: [{ _id: { month: 1..12 }, count }]
  const cells = Array.from({ length: 12 }, (_, i) => {
    const found = byMonth?.find((m) => m._id?.month === i + 1);
    return { month: MONTH_NAMES[i], count: found?.count || 0 };
  });
  const max = Math.max(...cells.map((c) => c.count), 1);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 4 }}>
      {cells.map(({ month, count }) => {
        const opacity = count === 0 ? 0.07 : 0.2 + (count / max) * 0.8;
        return (
          <div
            key={month}
            title={`${month}: ${count} records`}
            style={{
              background: color,
              opacity,
              borderRadius: 4,
              height: 36,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 9,
              fontWeight: 700,
              color: '#fff',
              cursor: 'default',
            }}
          >
            {month}
            <span style={{ fontSize: 10, fontWeight: 800, opacity: 1 }}>{count || ''}</span>
          </div>
        );
      })}
    </div>
  );
}

// Community comparison table row
export function CommunityRow({ community, maxHarvest, maxMembers }) {
  const h = community.harvest;
  const approvalRate = h.total > 0 ? Math.round((h.approved / h.total) * 100) : null;
  const harvestWidth = maxHarvest > 0 ? Math.round((h.total / maxHarvest) * 100) : 0;
  const memberWidth  = maxMembers > 0 ? Math.round((community.members / maxMembers) * 100) : 0;

  const MODULE_ICONS = {
    consultation:  '🗣️',
    harvesting:    '🌿',
    communityData: '📊',
    communityNews: '📰',
    lenses:        '🔭',
    sevenGen:      '🏹',
  };

  return (
    <div
      style={{
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
        padding: '16px 20px', marginBottom: 10,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <span style={{ fontWeight: 700, fontSize: 16 }}>{community.displayName}</span>
          {community.displayName !== community.name && (
            <span style={{ color: '#9ca3af', marginLeft: 6, fontSize: 12 }}>({community.name})</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {community.enabledModules.map((m) => (
            <span
              key={m}
              title={m}
              style={{
                background: '#f0fdf4', color: '#166534',
                borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 600,
              }}
            >
              {MODULE_ICONS[m] || '•'} {m}
            </span>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, fontSize: 13 }}>
        {/* Members */}
        <div>
          <div style={{ color: '#6b7280', marginBottom: 4 }}>Members</div>
          <div style={{ fontWeight: 700, fontSize: 18 }}>{community.members}</div>
          <div style={{ background: '#e0f2fe', borderRadius: 4, height: 6, marginTop: 4 }}>
            <div style={{ width: `${memberWidth}%`, height: '100%', background: '#0284c7', borderRadius: 4 }} />
          </div>
        </div>
        {/* Harvest */}
        <div>
          <div style={{ color: '#6b7280', marginBottom: 4 }}>Harvest records</div>
          <div style={{ fontWeight: 700, fontSize: 18 }}>
            {h.total}
            {h.pending > 0 && (
              <span style={{ fontSize: 11, background: '#fef9c3', color: '#854d0e', borderRadius: 8, padding: '1px 6px', marginLeft: 6 }}>
                {h.pending} pending
              </span>
            )}
          </div>
          <div style={{ background: '#dcfce7', borderRadius: 4, height: 6, marginTop: 4 }}>
            <div style={{ width: `${harvestWidth}%`, height: '100%', background: '#16a34a', borderRadius: 4 }} />
          </div>
          {approvalRate !== null && (
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{approvalRate}% approved</div>
          )}
        </div>
        {/* Consultation */}
        <div>
          <div style={{ color: '#6b7280', marginBottom: 4 }}>Consultation</div>
          <div style={{ fontWeight: 700, fontSize: 18 }}>{community.consultation.responses}</div>
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
            {community.consultation.questionnaires} questionnaire{community.consultation.questionnaires !== 1 ? 's' : ''}
          </div>
        </div>
      </div>
    </div>
  );
}
